import isNil from 'lodash/isNil'
import { isValidDueDay, MAX_DUE_DAY, MIN_DUE_DAY } from './types'

export type BulkUploadStep = 'select' | 'preview' | 'results'

export interface ParsedRow {
  rowNum: number
  clientName: string
  toEmails: string
  paymentType: string
  dueDay: string
  clientMatch?: boolean
}

export enum ErrorGroupKey {
  UNMATCHED_CLIENT = 'unmatchedClient',
  MISSING_CLIENT_NAME = 'missingClientName',
  INVALID_PAYMENT_TYPE = 'invalidPaymentType',
  INVALID_DATE = 'invalidDate',
}

export interface ErrorGroupRow {
  rowNum: number
  value?: string
}

export interface ErrorGroupConfig {
  key: ErrorGroupKey
  label: string
  hint?: string
  rows: ErrorGroupRow[]
}

export interface RowFieldErrors {
  paymentType?: string
  dueDay?: string
}

/** Payment type as it appears in the sheet once lower-cased for comparison. */
const NORMALIZED_WIRE = 'wire'
const NORMALIZED_STRIPE = 'stripe'

export const UPLOAD_PAYMENT_TYPE_LABELS = ['Wire', 'Stripe'] as const

export const UPLOAD_HEADERS = [
  'Client Name',
  'To Emails',
  'Payment Type',
  'Due Day',
] as const

export const UPLOAD_COLUMN_WIDTHS = [
  { wch: 25 },
  { wch: 40 },
  { wch: 15 },
  { wch: 14 },
]

export const TEMPLATE_ROWS = [
  {
    'Client Name': 'Acme Solutions Inc.',
    'To Emails': 'billing@acme.com; finance@acme.com',
    'Payment Type': 'Wire',
    'Due Day': '15',
  },
  {
    'Client Name': 'GlobalTech Corp',
    'To Emails': 'ap@globaltech.com',
    'Payment Type': 'Stripe',
    'Due Day': '1',
  },
]

/** True when the cell holds something other than Wire or Stripe. Blank passes,
 *  because a blank cell keeps the profile's current value. */
export const isInvalidPaymentType = (value: string): boolean => {
  const normalized = value.trim().toLowerCase()
  return (
    normalized !== '' &&
    normalized !== NORMALIZED_WIRE &&
    normalized !== NORMALIZED_STRIPE
  )
}

export const isInvalidDueDay = (value: string): boolean =>
  value !== '' && !isValidDueDay(value)

export const formatPaymentTypeLabel = (value: string | null): string => {
  if (isNil(value) || value === '') return ''
  const normalized = value.trim().toLowerCase()
  if (normalized === NORMALIZED_WIRE) return 'Wire'
  if (normalized === NORMALIZED_STRIPE) return 'Stripe'
  return value
}

export const hasInitialErrors = (row: ParsedRow): boolean =>
  row.clientMatch === false ||
  isInvalidPaymentType(row.paymentType) ||
  isInvalidDueDay(row.dueDay)

/** Groups every issue in the sheet by kind so the banner can list them, in the
 *  order they are presented to the user. Empty groups are dropped. */
export const buildErrorGroups = (rows: ParsedRow[]): ErrorGroupConfig[] => {
  const groups: Record<ErrorGroupKey, ErrorGroupRow[]> = {
    [ErrorGroupKey.UNMATCHED_CLIENT]: [],
    [ErrorGroupKey.MISSING_CLIENT_NAME]: [],
    [ErrorGroupKey.INVALID_PAYMENT_TYPE]: [],
    [ErrorGroupKey.INVALID_DATE]: [],
  }

  for (const row of rows) {
    if (row.clientName.trim() === '') {
      groups[ErrorGroupKey.MISSING_CLIENT_NAME].push({ rowNum: row.rowNum })
    } else if (row.clientMatch === false) {
      groups[ErrorGroupKey.UNMATCHED_CLIENT].push({
        rowNum: row.rowNum,
        value: row.clientName,
      })
    }

    if (isInvalidPaymentType(row.paymentType)) {
      groups[ErrorGroupKey.INVALID_PAYMENT_TYPE].push({
        rowNum: row.rowNum,
        value: row.paymentType,
      })
    }

    if (isInvalidDueDay(row.dueDay)) {
      groups[ErrorGroupKey.INVALID_DATE].push({
        rowNum: row.rowNum,
        value: row.dueDay,
      })
    }
  }

  const ordered: ErrorGroupConfig[] = [
    {
      key: ErrorGroupKey.UNMATCHED_CLIENT,
      label: 'Unmatched client name',
      hint: 'select the correct client from the dropdown',
      rows: groups[ErrorGroupKey.UNMATCHED_CLIENT],
    },
    {
      key: ErrorGroupKey.MISSING_CLIENT_NAME,
      label: 'Missing client name',
      hint: 'select a client from the dropdown',
      rows: groups[ErrorGroupKey.MISSING_CLIENT_NAME],
    },
    {
      key: ErrorGroupKey.INVALID_PAYMENT_TYPE,
      label: 'Invalid payment type',
      hint: 'must be Wire or Stripe',
      rows: groups[ErrorGroupKey.INVALID_PAYMENT_TYPE],
    },
    {
      key: ErrorGroupKey.INVALID_DATE,
      label: 'Invalid due day',
      hint: `must be a day from ${MIN_DUE_DAY} to ${MAX_DUE_DAY}`,
      rows: groups[ErrorGroupKey.INVALID_DATE],
    },
  ]

  return ordered.filter((group) => group.rows.length > 0)
}

const CLIENT_GROUP_KEYS = new Set<ErrorGroupKey>([
  ErrorGroupKey.UNMATCHED_CLIENT,
  ErrorGroupKey.MISSING_CLIENT_NAME,
])

export interface ErrorRowIndex {
  /** Rows with a bad cell value, highlighted red. */
  invalidRowNums: Set<number>
  /** Rows whose only problem is the client name, highlighted amber. */
  unmatchedRowNums: Set<number>
  /** Distinct rows carrying at least one issue. */
  totalErrorRowCount: number
  /** Issues in total, so one row with two problems counts twice. */
  totalIssueCount: number
  fieldErrorsByRow: Map<number, RowFieldErrors>
}

/** Single pass over the groups producing every lookup the preview table needs. */
export const buildErrorRowIndex = (
  groups: ErrorGroupConfig[]
): ErrorRowIndex => {
  const invalidRowNums = new Set<number>()
  const unmatchedRowNums = new Set<number>()
  const allRowNums = new Set<number>()
  const fieldErrorsByRow = new Map<number, RowFieldErrors>()
  let totalIssueCount = 0

  for (const group of groups) {
    const isClientGroup = CLIENT_GROUP_KEYS.has(group.key)
    totalIssueCount += group.rows.length

    for (const row of group.rows) {
      allRowNums.add(row.rowNum)
      if (isClientGroup) {
        unmatchedRowNums.add(row.rowNum)
        continue
      }
      invalidRowNums.add(row.rowNum)

      const existing = fieldErrorsByRow.get(row.rowNum) ?? {}
      if (group.key === ErrorGroupKey.INVALID_PAYMENT_TYPE) {
        fieldErrorsByRow.set(row.rowNum, {
          ...existing,
          paymentType: 'Must be Wire or Stripe',
        })
      } else if (group.key === ErrorGroupKey.INVALID_DATE) {
        fieldErrorsByRow.set(row.rowNum, {
          ...existing,
          dueDay: `Must be ${MIN_DUE_DAY}-${MAX_DUE_DAY}`,
        })
      }
    }
  }

  return {
    invalidRowNums,
    unmatchedRowNums,
    totalErrorRowCount: allRowNums.size,
    totalIssueCount,
    fieldErrorsByRow,
  }
}

/** Error rows first, original sheet order within each half. Applied once at
 *  parse time so fixing a row never makes it jump position mid-edit. */
export const sortRowsErrorsFirst = (rows: ParsedRow[]): ParsedRow[] =>
  [...rows].sort((a, b) => {
    const aErr = hasInitialErrors(a) ? 0 : 1
    const bErr = hasInitialErrors(b) ? 0 : 1
    if (aErr !== bErr) return aErr - bErr
    return a.rowNum - b.rowNum
  })
